import { jsPDF } from "jspdf";
import { PLAYBOOK_STEPS, PROGRAM_NAME } from "@/data/playbook";
import { STRATEGIST } from "@/data/strategist";

const EQUIPMENT_LABEL = {
  dry_van: "Dry Van", reefer: "Reefer", flatbed: "Flatbed",
  step_deck: "Step Deck", box_truck: "Box Truck", power_only: "Power Only",
};

const readJSON = (k) => {
  try {
    return JSON.parse(localStorage.getItem(k));
  } catch {
    return null;
  }
};

export function gatherReportData(coach = []) {
  const progress = (() => {
    const raw = readJSON("routehaul_progress_v1");
    return Array.isArray(raw) ? raw : [];
  })();
  return {
    generatedAt: new Date(),
    progress,
    diagnostic: readJSON("routehaul_diagnostic_v1"),
    cpm: readJSON("routehaul_cpm_v1"),
    coach: Array.isArray(coach) ? coach : [],
  };
}

const perMile = (n) => `$${(isFinite(n) ? n : 0).toFixed(3)}`;
const money = (n) => `$${(isFinite(n) ? n : 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export function generateReportPdf(data) {
  const { generatedAt, progress, diagnostic, cpm, coach } = data;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 48;
  const maxW = pageW - M * 2;
  let y = 0;

  const ensure = (h) => {
    if (y + h > pageH - M) {
      doc.addPage();
      y = M;
    }
  };
  const heading = (t) => {
    ensure(44);
    y += 10;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(15, 23, 42);
    doc.text(t, M, y);
    y += 8;
    doc.setDrawColor(234, 88, 12);
    doc.setLineWidth(2.5);
    doc.line(M, y, M + 46, y);
    y += 18;
  };
  const para = (t, { bold = false, size = 10, color = [51, 65, 85], indent = 0, lh = 14 } = {}) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(color[0], color[1], color[2]);
    const lines = doc.splitTextToSize(String(t), maxW - indent);
    lines.forEach((ln) => {
      ensure(lh);
      doc.text(ln, M + indent, y);
      y += lh;
    });
  };
  const bullets = (arr, color) => (arr || []).forEach((s) => para(`•  ${s}`, { indent: 8, color }));

  // Title banner
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, pageW, 96, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("Operator Report", M, 46);
  doc.setFontSize(10);
  doc.setTextColor(234, 88, 12);
  doc.text(PROGRAM_NAME.toUpperCase(), M, 66);
  doc.setTextColor(148, 163, 184);
  doc.text(`Generated ${generatedAt.toLocaleString()}`, M, 82);
  y = 124;

  // Playbook progress
  const done = progress.length;
  heading(`12-Step Business Foundation Guide  —  ${done}/12 complete`);
  PLAYBOOK_STEPS.forEach((s) => {
    const isDone = progress.includes(s.n);
    ensure(15);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    if (isDone) doc.setTextColor(16, 122, 87);
    else doc.setTextColor(100, 116, 139);
    doc.text(`${isDone ? "[x]" : "[  ]"}  ${s.n}. ${s.title}`, M, y);
    y += 15;
  });

  // Diagnostic
  heading("Readiness Diagnostic");
  if (diagnostic) {
    para(`Score: ${diagnostic.score}/100   (tier: ${diagnostic.tier})`, { bold: true, size: 12, color: [15, 23, 42] });
    if (diagnostic.ai_analysis?.headline) para(diagnostic.ai_analysis.headline, { bold: true, color: [234, 88, 12] });
    if (diagnostic.ai_analysis?.summary) para(diagnostic.ai_analysis.summary);
    if (diagnostic.ai_analysis?.strengths?.length) {
      y += 4; para("Strengths", { bold: true, color: [15, 23, 42] });
      bullets(diagnostic.ai_analysis.strengths, [16, 122, 87]);
    }
    if (diagnostic.ai_analysis?.gaps?.length) {
      y += 4; para("Gaps to close", { bold: true, color: [15, 23, 42] });
      bullets(diagnostic.ai_analysis.gaps, [180, 83, 9]);
    }
    if (diagnostic.ai_analysis?.next_steps?.length) {
      y += 4; para("Recommended next steps", { bold: true, color: [15, 23, 42] });
      bullets(diagnostic.ai_analysis.next_steps);
    }
    if (diagnostic.answers?.length) {
      y += 6; para("Your answers", { bold: true, color: [15, 23, 42] });
      diagnostic.answers.forEach((a) =>
        para(`${a.question}  —  ${a.answer} (${a.points}/${a.max_points})`, { indent: 8, size: 9 })
      );
    }
  } else {
    para("No diagnostic completed yet. Run the 2-minute readiness diagnostic to include your score here.", { color: [148, 163, 184] });
  }

  // CPM worksheet
  heading("Cost-Per-Mile Worksheet");
  if (cpm?.results) {
    const i = cpm.inputs || {};
    const rr = cpm.results;
    para(`Equipment: ${EQUIPMENT_LABEL[cpm.equipment] || cpm.equipment || "—"}`, { bold: true, color: [15, 23, 42] });
    y += 4;
    para("Inputs", { bold: true, color: [15, 23, 42] });
    para(`Truck/trailer payment: ${money(i.payment)}/mo   Insurance: ${money(i.insurance)}/mo   Permits: ${money(i.permits)}/mo   Other fixed: ${money(i.otherFixed)}/mo`, { indent: 8, size: 9 });
    para(`Diesel: $${i.diesel}/gal   Fuel economy: ${i.mpg} MPG   Maintenance: $${i.maintenance}/mi   Tires: $${i.tires}/mi   Tolls/misc: $${i.tollsMisc}/mi`, { indent: 8, size: 9 });
    para(`Miles/month: ${Number(i.miles).toLocaleString()}   Target margin: ${i.margin}%`, { indent: 8, size: 9 });
    y += 4;
    para("Results", { bold: true, color: [15, 23, 42] });
    para(`Break-even cost per mile: ${perMile(rr.totalCPM)}`, { indent: 8, bold: true, color: [234, 88, 12] });
    para(`Suggested rate (+${i.margin}% margin): ${perMile(rr.suggestedRate)}`, { indent: 8, bold: true, color: [16, 122, 87] });
    para(`Fixed cost/mile: ${perMile(rr.fixedCPM)}   Fuel cost/mile: ${perMile(rr.fuelCPM)}   Variable cost/mile: ${perMile(rr.variableCPM)}`, { indent: 8, size: 9 });
    para(`Fixed costs/month: ${money(rr.fixedMonthly)}   Total operating cost/month: ${money(rr.monthlyOperating)}`, { indent: 8, size: 9 });
  } else {
    para("No CPM worksheet saved yet. Open the CPM Calculator and enter your numbers to include them here.", { color: [148, 163, 184] });
  }

  // Coach transcript
  if (coach?.length) {
    heading("AI Coach Transcript");
    coach.forEach((m) => {
      const isUser = m.role === "user";
      para(`${isUser ? "You" : "Coach"}:`, { bold: true, color: isUser ? [15, 23, 42] : [234, 88, 12], size: 10 });
      para(m.content, { indent: 8, size: 9 });
      y += 4;
    });
  }

  // Strategist contact
  heading("Work With a Lane Strategist");
  para(`${STRATEGIST.name} — ${STRATEGIST.title}`, { bold: true, color: [15, 23, 42], size: 11 });
  para(STRATEGIST.business, { color: [100, 116, 139] });
  para(`Email: ${STRATEGIST.email}`, { bold: true, color: [234, 88, 12] });
  para(STRATEGIST.blurb, { size: 9 });

  // Disclaimer footer on each page
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(
      `${PROGRAM_NAME}  ·  Estimates & educational guidance only — verify with FMCSA or a professional.  ·  Page ${p}/${pages}`,
      M,
      pageH - 24
    );
  }

  const stamp = data.generatedAt.toISOString().slice(0, 10);
  doc.save(`routehaul-operator-report-${stamp}.pdf`);
}
